import { useTranslation } from 'react-i18next';
import { unitLabel } from '@d-orca/shared/client/logistics';
import type { InventoryLevel } from '@interfaces/ILogistics';
import { PersonIcon } from '@utils/icons';
import { Tooltip, TooltipContent } from '@components/ui/Tooltip/Tooltip';
import { ReorderTickTank } from '../ReorderTickTank';
import { addDays, formatDay, zonedDay, rowTone, orderByDate } from '../logistics.format';
import styles from './InventoryLevels.module.css';

interface IInventoryRowCoreProps {
  level: InventoryLevel;
  /** The PROJECT's IANA timezone — see `frontend/CLAUDE.md` invariant #6. */
  tz: string;
}

/**
 * The read-only presentational core of one inventory row: name, the two meta
 * lines (amount/rate, then started-date/provenance/lead), the days-left
 * badge + duration cap, and the `ReorderTickTank` gauge.
 *
 * Split out of `InventoryRow` so the SiteMap AreaDrawer's stock rows can
 * mirror the Inventory Levels card byte-for-byte instead of a re-styled
 * lookalike — the two surfaces share this component and can never drift.
 * `InventoryRow` wraps `InventoryRowMain` inside its own `.main` div (adding
 * the kebab menu, reorder hints and sync/renew actions around it);
 * `InventoryRowGauge` is rendered as its sibling, same as before.
 */
export function InventoryRowMain({ level, tz }: IInventoryRowCoreProps) {
  const { t, i18n } = useTranslation();
  const entered = formatDay(zonedDay(level.enteredAt, tz), i18n.language);
  // The reorder deadline the lead implies — the SAME day `ReorderTickTank`
  // flips tone on. Surfaced in the "Delivery takes ~Nd" segment's tooltip
  // title (not the visible line itself, which stays in plain lead-time
  // language). null when there is no lead to project a deadline from —
  // gates the whole segment, mirroring the old "Lead Nd" segment's own
  // omission rule.
  const orderBy = orderByDate(level, tz);
  // Stored as a key (or a legacy label in either language): read in the
  // viewer's language.
  const unit = unitLabel(t, level.unit);

  return (
    <>
      <span className={styles.name}>{level.materialName}</span>
      {level.needsCheck ? (
        <span className={styles.meta}>
          {/* Both ends of the estimate's life — see `InventoryRow`'s own note
              on why "ran out" needs a date, not just a start. */}
          {t('logistics.inventory.enteredBare', {
            amount: level.amount,
            unit,
            d: entered,
            end: formatDay(addDays(zonedDay(level.enteredAt, tz), level.spanDays), i18n.language),
          })}
        </span>
      ) : (
        <>
          <span className={styles.meta}>
            {t('logistics.inventory.amountUnit', { amount: level.amount, unit })}
            {' · '}
            {t('logistics.inventory.perDay', { rate: level.dailyRate, unit })}
          </span>
          <span className={styles.provenance}>
            {t('logistics.inventory.entered', { d: entered })}
            {' · '}
            <span className={styles.provName}>
              <PersonIcon size={10} />
              {level.enteredBy}
            </span>
            {orderBy != null && level.leadSource != null ? (
              <>
                {' · '}
                <Tooltip
                  variant="hint"
                  wrap
                  content={
                    <TooltipContent
                      title={t('logistics.inventory.orderByTip.title', {
                        date: formatDay(orderBy, i18n.language),
                        // Non-null by construction: `orderBy` is null
                        // whenever `leadTimeDays` is, and this branch is
                        // gated on it.
                        count: level.leadTimeDays!,
                      })}
                      lines={[
                        t(`logistics.inventory.orderByTip.source.${level.leadSource}`),
                        t('logistics.inventory.orderByTip.risk'),
                      ]}
                    />
                  }
                >
                  <span className={styles.deliveryTakes}>
                    {t('logistics.inventory.deliveryTakes', { n: level.leadTimeDays })}
                  </span>
                </Tooltip>
              </>
            ) : null}
          </span>
        </>
      )}
    </>
  );
}

/** The row's days-left badge: the dashed "Ran out" on a needsCheck row, else
 *  "Nd left" in the row's tone (`rowTone`). Also the stock drawer's header
 *  pill, so a row and its drawer can never disagree about how urgent it is. */
export function StockBadge({ level, tz }: IInventoryRowCoreProps) {
  const { t } = useTranslation();
  return level.needsCheck ? (
    <span className={styles.badge} data-tone="check">
      {t('logistics.inventory.needsCheck')}
    </span>
  ) : (
    <span className={styles.badge} data-tone={rowTone(level, tz)}>
      {t('logistics.inventory.daysLeft', { n: level.daysLeft })}
    </span>
  );
}

/** The row's right-hand gauge cluster: days-left badge + duration cap
 *  (tracked rows) or the dashed "Ran out" pill (needsCheck), plus the
 *  `ReorderTickTank` — which itself renders nothing for a needsCheck row. */
export function InventoryRowGauge({ level, tz }: IInventoryRowCoreProps) {
  const { t } = useTranslation();

  return (
    <>
      <span className={styles.side}>
        <StockBadge level={level} tz={tz} />
        {level.needsCheck ? null : (
          <span className={styles.durationCap} data-tone={rowTone(level, tz)}>
            {t('logistics.inventory.duration', { n: level.spanDays })}
          </span>
        )}
      </span>
      <ReorderTickTank level={level} tz={tz} className={styles.tankInRow} />
    </>
  );
}
